'use client';

import { c as _c } from "react/compiler-runtime";
import { CurrentMenuPathContext } from '@/components/menu/app-feature-link';
import { appFeaturesTitle } from '@/components/menu/app-features-title';
import type { ProcessedMainMenuItem } from '@/components/menu/main-menu';
import { moreColumnLabel, splitMenuColumns } from '@/components/menu/menu-columns';
import type { MenuNode } from '@/components/menu/menu-links';
import { MenuEntryLabel, MenuLinks, visibleChildren } from '@/components/menu/menu-links';
import { useFittingItemCount } from '@/components/menu/use-fitting-item-count';
import { languageOptions } from '@/config/language-options';
import { InstantSearchDialog } from '@/features/payload-cms/components/search/instant-search-dialog';
import { useSearchShortcut } from '@/features/payload-cms/components/search/use-search-shortcut';
import { usePreviewMode } from '@/hooks/use-preview-mode';
import type { Locale, StaticTranslationString } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { ChevronDown, Languages, Menu as MenuIcon, Search, X } from 'lucide-react';
import { usePathname, useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
const menuButtonLabel: StaticTranslationString = {
  de: 'Menü',
  fr: 'Menu',
  en: 'Menu'
};
const searchLabel: StaticTranslationString = {
  de: 'Suchen',
  fr: 'Rechercher',
  en: 'Search'
};
const pinnedItemClassName = 'flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-semibold whitespace-nowrap';
const pinnedRowClassName = 'flex items-center gap-1 xl:gap-1.5';
const columnHeadingClassName = 'font-heading border-line mb-1.5 block border-b px-3 pb-3.5 text-[13px] font-extrabold tracking-[0.09em] text-green-600 uppercase';

/**
 * Calls `onDismiss` on a pointer press outside every referenced element, or on Escape, while `isOpen`.
 */
const useDismissOnOutsideInteraction = (references, isOpen, onDismiss) => {
  const $ = _c(5);
  let t0;
  let t1;
  if ($[0] !== isOpen || $[1] !== onDismiss || $[2] !== references) {
    t0 = () => {
      if (!isOpen) {
        return;
      }
      const handlePointerDown = event => {
        const target = event.target;
        if (!(target instanceof Node)) {
          return;
        }
        if (references.some(reference => reference.current?.contains(target) === true)) {
          return;
        }
        onDismiss();
      };
      const handleKeyDown = event_0 => {
        if (event_0.key === "Escape") {
          onDismiss();
        }
      };
      document.addEventListener("pointerdown", handlePointerDown);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("pointerdown", handlePointerDown);
        document.removeEventListener("keydown", handleKeyDown);
      };
    };
    t1 = [references, isOpen, onDismiss];
    $[0] = isOpen;
    $[1] = onDismiss;
    $[2] = references;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
};

/**
 * Moves the focus into the column `sectionId` names once the menu opened on it, so a keyboard
 * continues where the pinned entry sent it, and scrolls the column into view on short screens.
 */
const useFocusMenuSection = (panel, sectionId) => {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== panel || $[1] !== sectionId) {
    t0 = () => {
      if (sectionId === undefined) {
        return;
      }
      const section = panel.current?.querySelector(`[data-menu-section="${sectionId}"]`);
      section?.scrollIntoView({
        block: "nearest"
      });
      section?.querySelector("a, [tabindex]")?.focus({
        preventScroll: true
      });
    };
    t1 = [panel, sectionId];
    $[0] = panel;
    $[1] = sectionId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
};

/**
 * The opened menu: every column side by side, in the order the editors gave them, and the entries
 * without entries below them collected in a last column. Opened from a pinned entry, the focus
 * moves into that entry's column. In the app, the app's features come first.
 */
const MenuPanel: React.FC<{
  id: string;
  locale: Locale;
  menuItems: ProcessedMainMenuItem[];
  appFeatures: ReactNode;
  highlightedId: string | undefined;
  isCurrent: (node: MenuNode) => boolean;
  panelReference: React.RefObject<HTMLDivElement | null>;
  onLinkClick: (event: React.MouseEvent) => void;
}> = t0 => {
  const $ = _c(35);
  const {
    id,
    locale,
    menuItems,
    appFeatures,
    highlightedId,
    isCurrent,
    panelReference,
    onLinkClick
  } = t0;
  useFocusMenuSection(panelReference, highlightedId);
  let more;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== appFeatures || $[1] !== id || $[2] !== isCurrent || $[3] !== locale || $[4] !== menuItems || $[5] !== onLinkClick || $[6] !== panelReference) {
    const items = menuItems.filter(_temp).map(_temp2);
    const {
      columns,
      more: t8
    } = splitMenuColumns(items);
    more = t8;
    t4 = id;
    t5 = panelReference;
    t6 = onLinkClick;
    t7 = "animate-in fade-in-0 slide-in-from-top-1 border-line fixed top-[calc(4rem+var(--app-top-inset))] right-0 left-0 z-50 max-h-[calc(100dvh-4rem-var(--app-top-inset))] overflow-y-auto border-b-2 bg-white duration-150";
    t1 = "mx-auto grid max-w-7xl grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-x-6 gap-y-8 px-12 py-10";
    if ($[15] !== appFeatures || $[16] !== locale) {
      t2 = appFeatures !== undefined && <section aria-label={appFeaturesTitle[locale]} className="-m-3 p-3"><span className={columnHeadingClassName}>{appFeaturesTitle[locale]}</span>{appFeatures}</section>;
      $[15] = appFeatures;
      $[16] = locale;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    let t9;
    if ($[18] !== isCurrent) {
      t9 = item_1 => <section key={item_1.id} data-menu-section={item_1.id} aria-label={item_1.label} className="-m-3 p-3"><MenuEntryLabel node={item_1} isCurrent={isCurrent(item_1)} className={cn(columnHeadingClassName, item_1.hasLink && "hover:text-heading")} /><MenuLinks nodes={item_1.subMenu} showPreview={false} isCurrent={isCurrent} /></section>;
      $[18] = isCurrent;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    t3 = columns.map(t9);
    $[0] = appFeatures;
    $[1] = id;
    $[2] = isCurrent;
    $[3] = locale;
    $[4] = menuItems;
    $[5] = onLinkClick;
    $[6] = panelReference;
    $[7] = more;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    more = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
  }
  let t8;
  if ($[20] !== isCurrent || $[21] !== locale || $[22] !== more) {
    t8 = more.length > 0 && <section aria-label={moreColumnLabel[locale]} className="-m-3 p-3"><span className={columnHeadingClassName}>{moreColumnLabel[locale]}</span><MenuLinks nodes={more} showPreview={false} isCurrent={isCurrent} /></section>;
    $[20] = isCurrent;
    $[21] = locale;
    $[22] = more;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t1 || $[25] !== t2 || $[26] !== t3 || $[27] !== t8) {
    t9 = <div className={t1}>{t2}{t3}{t8}</div>;
    $[24] = t1;
    $[25] = t2;
    $[26] = t3;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t4 || $[30] !== t5 || $[31] !== t6 || $[32] !== t7 || $[33] !== t9) {
    t10 = <div id={t4} ref={t5} onClick={t6} className={t7}>{t9}</div>;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  return t10;
};
const PinnedItem: React.FC<{
  item: ProcessedMainMenuItem;
  isOpen: boolean;
  isCurrent: boolean;
  menuId: string;
  onOpen: () => void;
}> = t0 => {
  const $ = _c(21);
  const {
    item,
    isOpen,
    isCurrent,
    menuId,
    onOpen
  } = t0;
  if (visibleChildren(item, false).length > 0) {
    const t1 = isOpen ? "bg-tint text-heading" : "hover:bg-tint hover:text-heading text-gray-700";
    let t2;
    if ($[0] !== t1) {
      t2 = cn(pinnedItemClassName, "transition-colors duration-150", t1);
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== item.label) {
      t3 = <span>{item.label}</span>;
      $[2] = item.label;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    const t4 = isOpen && "rotate-180";
    let t5;
    if ($[4] !== t4) {
      t5 = cn("size-4 text-gray-400 transition-transform", t4);
      $[4] = t4;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== t5) {
      t6 = <ChevronDown aria-hidden={true} className={t5} />;
      $[6] = t5;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== isOpen || $[9] !== menuId || $[10] !== onOpen || $[11] !== t2 || $[12] !== t3 || $[13] !== t6) {
      t7 = <button type="button" aria-expanded={isOpen} aria-controls={menuId} onClick={onOpen} className={t2}>{t3}{t6}</button>;
      $[8] = isOpen;
      $[9] = menuId;
      $[10] = onOpen;
      $[11] = t2;
      $[12] = t3;
      $[13] = t6;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    return t7;
  }
  const t1 = isCurrent && "text-heading underline decoration-2 underline-offset-8";
  let t2;
  if ($[15] !== t1) {
    t2 = cn(pinnedItemClassName, "hover:bg-tint hover:text-heading text-gray-700 transition-colors duration-150", t1);
    $[15] = t1;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] !== isCurrent || $[18] !== item || $[19] !== t2) {
    t3 = <MenuEntryLabel node={item} isCurrent={isCurrent} className={t2} />;
    $[17] = isCurrent;
    $[18] = item;
    $[19] = t2;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  return t3;
};

/**
 * The header bar of the website on large screens: the entries the editors pinned, the language,
 * the search, and "Menü", which opens the whole menu below the bar.
 */
export const DesktopNav: React.FC<{
  locale: Locale;
  menuItems: ProcessedMainMenuItem[];
  /** the draft menu, handed over for an editor only and shown in preview mode */
  draftMenuItems?: ProcessedMainMenuItem[] | undefined;
  actionURL: string;
  /** the app's features, the first column of the menu in the app */
  appFeatures?: ReactNode;
}> = t0 => {
  const $ = _c(86);
  const {
    locale,
    menuItems: publishedMenuItems,
    draftMenuItems,
    actionURL,
    appFeatures
  } = t0;
  const searchParameters = useSearchParams();
  const {
    isInPreviewMode
  } = usePreviewMode();
  const menuItems = isInPreviewMode === "enabled" && draftMenuItems !== undefined ? draftMenuItems : publishedMenuItems;
  let t1;
  if ($[0] !== searchParameters) {
    t1 = () => searchParameters.get("menu") === "open" ? true : undefined;
    $[0] = searchParameters;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [openedFrom, setOpenedFrom] = useState(t1);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsSearchOpen(true);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const openSearch = t2;
  useSearchShortcut(openSearch);
  const menuId = useId();
  const barReference = useRef(null);
  const pinnedReference = useRef(null);
  const measureRowReference = useRef(null);
  const panelReference = useRef(null);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [barReference];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const dismissReferences = t3;
  const pathname = usePathname();
  const pinnedItems = menuItems.filter(_temp3);
  const fittingCount = useFittingItemCount(pinnedReference, measureRowReference, pinnedItems.length);
  const fittingItems = pinnedItems.slice(0, fittingCount);
  let t4;
  if ($[4] !== appFeatures || $[5] !== menuItems) {
    t4 = appFeatures !== undefined || menuItems.some(_temp4);
    $[4] = appFeatures;
    $[5] = menuItems;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const hasMenu = t4;
  const isMenuOpen = openedFrom !== undefined;
  const highlightedId = typeof openedFrom === "string" ? openedFrom : undefined;
  let t5;
  if ($[7] !== pathname) {
    t5 = node => node.hasLink && node.itemLink === pathname;
    $[7] = pathname;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const isCurrent = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setOpenedFrom(undefined);
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const closeMenu = t6;
  useDismissOnOutsideInteraction(dismissReferences, isMenuOpen, closeMenu);
  let t7;
  if ($[10] !== closeMenu) {
    t7 = event => {
      if (event.target instanceof Element && event.target.closest("a") !== null) {
        closeMenu();
      }
    };
    $[10] = closeMenu;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const closeOnLinkClick = t7;
  let t8;
  if ($[12] !== pathname || $[13] !== searchParameters) {
    t8 = lang => {
      const langRegex = /^\/(de|en|fr)\//;
      let newPath;
      const searchParametersString = searchParameters.toString();
      const searchParameterPrefixed = searchParametersString === "" ? "" : `?${searchParametersString}`;
      if (langRegex.test(pathname)) {
        newPath = pathname.replace(langRegex, `/${lang}/`) + searchParameterPrefixed;
      } else {
        const path = pathname.replace(/\/(de|en|fr)\/?$/, "");
        const cleanPath = path.startsWith("//") ? path.slice(1) : path;
        const pathWithoutLeadingSlash = cleanPath.replace(/^\//, "");
        newPath = `/${lang}/${pathWithoutLeadingSlash}${searchParameterPrefixed}`;
      }
      if (newPath.endsWith("/") && newPath.length > 1) {
        newPath = newPath.slice(0, -1);
      }
      globalThis.location.assign(newPath);
    };
    $[12] = pathname;
    $[13] = searchParameters;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleLanguageChange = t8;
  const t9 = "hidden min-w-0 flex-1 items-center gap-4 xl:flex";
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cn("relative min-w-0 flex-1 justify-end-safe overflow-hidden", pinnedRowClassName);
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== highlightedId || $[17] !== isCurrent || $[18] !== menuId) {
    t11 = item_1 => <PinnedItem key={item_1.id} item={item_1} isOpen={highlightedId === item_1.id} isCurrent={isCurrent(item_1)} menuId={menuId} onOpen={() => setOpenedFrom(highlightedId === item_1.id ? undefined : item_1.id)} />;
    $[16] = highlightedId;
    $[17] = isCurrent;
    $[18] = menuId;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = fittingItems.map(t11);
  const t13 = true;
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = cn("pointer-events-none invisible absolute top-0 left-0", pinnedRowClassName);
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const t15 = pinnedItems.map(_temp5);
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span />;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== t14 || $[23] !== t15) {
    t17 = <div ref={measureRowReference} aria-hidden={t13} className={t14}>{t15}{t16}</div>;
    $[22] = t14;
    $[23] = t15;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== closeOnLinkClick || $[26] !== t10 || $[27] !== t12 || $[28] !== t17) {
    t18 = <nav ref={pinnedReference} onClick={closeOnLinkClick} className={t10}>{t12}{t17}</nav>;
    $[25] = closeOnLinkClick;
    $[26] = t10;
    $[27] = t12;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  let t20;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setIsLangOpen(true);
    t20 = () => setIsLangOpen(false);
    $[30] = t19;
    $[31] = t20;
  } else {
    t19 = $[30];
    t20 = $[31];
  }
  let t21;
  if ($[32] !== isLangOpen) {
    t21 = () => setIsLangOpen(!isLangOpen);
    $[32] = isLangOpen;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Languages className="text-conveniat-green size-3.5" />;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== locale) {
    t23 = <span className="uppercase">{locale}</span>;
    $[35] = locale;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  const t24 = isLangOpen && "rotate-180";
  let t25;
  if ($[37] !== t24) {
    t25 = cn("size-3 text-gray-400 transition-transform", t24);
    $[37] = t24;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] !== t25) {
    t26 = <ChevronDown className={t25} />;
    $[39] = t25;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  let t27;
  if ($[41] !== t21 || $[42] !== t23 || $[43] !== t26) {
    t27 = <button type="button" onClick={t21} className="hover:border-conveniat-green/40 hover:text-conveniat-green flex h-8 items-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50/80 px-2.5 text-xs font-semibold text-gray-700 transition-all duration-200 hover:bg-white">{t22}{t23}{t26}</button>;
    $[41] = t21;
    $[42] = t23;
    $[43] = t26;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== handleLanguageChange || $[46] !== isLangOpen || $[47] !== locale) {
    t28 = isLangOpen && <div className="absolute top-full right-0 z-50 w-32 pt-2"><div className="animate-in fade-in-0 slide-in-from-top-1 rounded-xl border border-gray-200/80 bg-white/95 p-1.5 backdrop-blur-xl duration-150">{languageOptions.map(t29 => {
          const {
            value,
            label
          } = t29;
          return <button key={value} type="button" onClick={() => handleLanguageChange(value)} className={cn("block w-full rounded-lg px-3 py-1.5 text-left text-xs font-semibold transition-colors", locale === value ? "bg-conveniat-green/10 text-conveniat-green font-bold" : "hover:text-conveniat-green text-gray-700 hover:bg-gray-100")}>{label}</button>;
        })}</div></div>;
    $[45] = handleLanguageChange;
    $[46] = isLangOpen;
    $[47] = locale;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] !== t27 || $[50] !== t28) {
    t29 = <div className="relative" onMouseEnter={t19} onMouseLeave={t20}>{t27}{t28}</div>;
    $[49] = t27;
    $[50] = t28;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  const t30 = searchLabel[locale];
  const t31 = `${searchLabel[locale]} (Ctrl K)`;
  let t32;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Search className="size-3.5 text-gray-500" aria-hidden="true" />;
    $[52] = t32;
  } else {
    t32 = $[52];
  }
  let t33;
  if ($[53] !== t30 || $[54] !== t31) {
    t33 = <button type="button" onClick={openSearch} aria-label={t30} aria-keyshortcuts="Control+K Meta+K" title={t31} className="hover:border-conveniat-green/40 hover:text-conveniat-green flex size-8 items-center justify-center rounded-xl border border-gray-200 bg-gray-50/80 text-gray-600 transition-all duration-200 hover:bg-white">{t32}</button>;
    $[53] = t30;
    $[54] = t31;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  let t34;
  if ($[56] !== t29 || $[57] !== t33) {
    t34 = <div className="relative flex h-8 shrink-0 items-center gap-2 border-l border-gray-200/80 pl-3">{t29}{t33}</div>;
    $[56] = t29;
    $[57] = t33;
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  let t35;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => setIsSearchOpen(false);
    $[59] = t35;
  } else {
    t35 = $[59];
  }
  const t36 = `/${locale}${actionURL}`;
  let t37;
  if ($[60] !== isSearchOpen || $[61] !== locale || $[62] !== t36) {
    t37 = <InstantSearchDialog open={isSearchOpen} onClose={t35} locale={locale} searchPath={t36} />;
    $[60] = isSearchOpen;
    $[61] = locale;
    $[62] = t36;
    $[63] = t37;
  } else {
    t37 = $[63];
  }
  let t38;
  if ($[64] !== hasMenu || $[65] !== isMenuOpen || $[66] !== locale || $[67] !== menuId) {
    t38 = hasMenu && <button type="button" aria-expanded={isMenuOpen} aria-controls={menuId} onClick={() => setOpenedFrom(isMenuOpen ? undefined : true)} className="bg-conveniat-green flex h-9 shrink-0 items-center gap-2 rounded-xl px-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90">{isMenuOpen ? <X aria-hidden={true} className="size-4" /> : <MenuIcon aria-hidden={true} className="size-4" />}{menuButtonLabel[locale]}</button>;
    $[64] = hasMenu;
    $[65] = isMenuOpen;
    $[66] = locale;
    $[67] = menuId;
    $[68] = t38;
  } else {
    t38 = $[68];
  }
  let t39;
  if ($[69] !== appFeatures || $[70] !== closeMenu || $[71] !== closeOnLinkClick || $[72] !== highlightedId || $[73] !== isCurrent || $[74] !== isMenuOpen || $[75] !== locale || $[76] !== menuId || $[77] !== menuItems || $[78] !== pathname) {
    t39 = isMenuOpen && <><div aria-hidden={true} onClick={closeMenu} className="animate-in fade-in-0 fixed inset-x-0 top-[calc(4rem+var(--app-top-inset))] z-40 h-dvh bg-gray-950/30" /><CurrentMenuPathContext value={pathname}><MenuPanel id={menuId} locale={locale} menuItems={menuItems} appFeatures={appFeatures} highlightedId={highlightedId} isCurrent={isCurrent} panelReference={panelReference} onLinkClick={closeOnLinkClick} /></CurrentMenuPathContext></>;
    $[69] = appFeatures;
    $[70] = closeMenu;
    $[71] = closeOnLinkClick;
    $[72] = highlightedId;
    $[73] = isCurrent;
    $[74] = isMenuOpen;
    $[75] = locale;
    $[76] = menuId;
    $[77] = menuItems;
    $[78] = pathname;
    $[79] = t39;
  } else {
    t39 = $[79];
  }
  let t40;
  if ($[80] !== t18 || $[81] !== t34 || $[82] !== t37 || $[83] !== t38 || $[84] !== t39) {
    t40 = <div ref={barReference} className={t9}>{t18}{t34}{t37}{t38}{t39}</div>;
    $[80] = t18;
    $[81] = t34;
    $[82] = t37;
    $[83] = t38;
    $[84] = t39;
    $[85] = t40;
  } else {
    t40 = $[85];
  }
  return t40;
};
function _temp(item) {
  return item.isVisible;
}
function _temp2(item_0) {
  return {
    ...item_0,
    subMenu: visibleChildren(item_0, false)
  };
}
function _temp3(item) {
  return item.isVisible && item.isPinned;
}
function _temp4(item_0) {
  return item_0.isVisible;
}
function _temp5(item_2) {
  return <span key={item_2.id} className={pinnedItemClassName}><span>{item_2.label}</span>{visibleChildren(item_2, false).length > 0 && <ChevronDown className="size-4" />}</span>;
}